import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Min 8 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success(t("auth.token_used_success"));
      setTimeout(() => nav("/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <div className="px-6 sm:px-12 py-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="reset-back">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t("auth.back_to_login")}
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">/ Reset</div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mb-2">{t("auth.new_password")}</h1>
          <p className="text-muted-foreground mb-10">Pick a strong one this time 😉</p>
          {done ? (
            <div className="rounded-xl border border-border bg-card/40 p-6 flex items-center gap-3" data-testid="reset-success">
              <div className="h-10 w-10 rounded-full brand-bg text-black flex items-center justify-center">
                <Check className="h-5 w-5" strokeWidth={2} />
              </div>
              <div>
                <div className="font-medium">{t("auth.token_used_success")}</div>
                <div className="text-xs text-muted-foreground">Redirecting…</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
              <div className="space-y-2">
                <Label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{t("auth.new_password")}</Label>
                <PasswordInput required value={pw} onChange={(e) => setPw(e.target.value)} className="h-12 bg-muted border-border" placeholder="at least 8 chars" data-testid="reset-password-input" autoComplete="new-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 brand-bg text-black font-semibold hover:opacity-90 active:scale-[0.98] transition" data-testid="reset-submit-btn">
                {loading ? t("common.loading") : t("auth.reset_password")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
